import _ from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import Fuse from 'fuse.js';

// Components
import { AssessmentSessionList } from './assessment-session-list.component';
import { useCompanySportTerminology } from '../../../lib/stores/company.store';
import { SportTerminology } from '../../../lib/types';
import { pluralTerm } from '../../../lib/helpers/term.helper';

interface IAssessmentSessionListContainer {
  assessmentSessions: IAssessmentSession[];
  countArchivedSessions: number;
  countClosedSessions: number;
  loading: boolean;
  fetchError: boolean;
  viewingArchived: boolean;
  setViewingArchived: Function;
  viewingClosed: boolean;
  setViewingClosed: Function;
  removeSession: Function;
  restoreSession: Function;
  closeSession: Function;
  openSession: Function;
}

export const AssessmentSessionListContainer: React.FC<IAssessmentSessionListContainer> = ({
  assessmentSessions,
  countArchivedSessions,
  countClosedSessions,
  loading,
  fetchError,
  viewingArchived,
  setViewingArchived,
  viewingClosed,
  setViewingClosed,
  removeSession,
  restoreSession,
  closeSession,
  openSession
}) => {
  const termLookup = useCompanySportTerminology()

  const [selected, setSelected] = useState<IAssessmentSession[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredAssessmentSessions, setFilteredAssessmentSessions] = useState(assessmentSessions);
  const fuse = new Fuse(assessmentSessions, { threshold: 0.3, keys: [
      'location',
      'address',
      'date',
      'time'
    ] });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (searchTerm.length) {
      const results = fuse.search(searchTerm);

      setFilteredAssessmentSessions(_.map(results, 'item'));
    } else {
      setFilteredAssessmentSessions(assessmentSessions);
    }
  }, [assessmentSessions, searchTerm]);

  const debounceSearch = useCallback(_.debounce((nextSearch: string) => {
    setSearchTerm(nextSearch)
  }, 300), []);

  const sessionsTerm = pluralTerm(termLookup[SportTerminology.SESSION]).toLowerCase();

  const runSessionMutations = (operation: string, verb: string, run: () => Promise<any>[]) =>
    Promise.all(run()).catch((error) => {
      console.error(`${operation} failed:`, error);
      window.alert(`Some ${sessionsTerm} could not be ${verb}. Please refresh and try again.`);
    });

  // One prompt covers every selected session whose notification setting is on; the others are never announced.
  const removeSessions = (_ids: string[]) => {
    const sessionsById = _.keyBy(assessmentSessions, '_id');
    const notifiableIds = _ids.filter(_id => sessionsById[_id]?.isPlayerNotificationEnabled === true);
    const sessionLabel = notifiableIds.length === 1
      ? termLookup[SportTerminology.SESSION].toLowerCase()
      : `${notifiableIds.length} ${sessionsTerm}`;
    const notify = notifiableIds.length > 0 && window.confirm(
      `Do you want to notify all ${pluralTerm(termLookup[SportTerminology.ATHLETE].toLowerCase())} of the deleted ${sessionLabel}?`
    );

    return runSessionMutations('removeAssessmentSession', 'deleted', () =>
      _ids.map(_id => removeSession(_id, notify && notifiableIds.includes(_id))));
  };

  const restoreSessions = (_ids: string[]) =>
    runSessionMutations('restoreAssessmentSession', 'restored', () => _ids.map(_id => restoreSession(_id)));

  const closeSessions = (_ids: string[]) =>
    runSessionMutations('closeAssessmentSession', 'closed', () => _ids.map(_id => closeSession(_id)));

  const openSessions = (_ids: string[]) =>
    runSessionMutations('openAssessmentSession', 'reopened', () => _ids.map(_id => openSession(_id)));

  return <AssessmentSessionList
    loading={loading}
    fetchError={fetchError}
    assessmentSessions={filteredAssessmentSessions}
    countArchivedSessions={countArchivedSessions}
    countClosedSessions={countClosedSessions}
    search={debounceSearch}
    selected={selected}
    setSelected={setSelected}
    viewingArchived={viewingArchived}
    setViewingArchived={setViewingArchived}
    viewingClosed={viewingClosed}
    setViewingClosed={setViewingClosed}
    removeSessions={removeSessions}
    restoreSessions={restoreSessions}
    closeSessions={closeSessions}
    openSessions={openSessions}
  />;
};
